import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import type { NewsItem } from '@/lib/types';
import { cn } from '@/lib/utils';

// Persisted high-water mark — the timestamp of the newest headline the user
// has already seen. Anything newer counts as unread and bumps the badge.
const SEEN_KEY = 'telos-notifs-seen';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (!isFinite(diff)) return '';
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function impactClass(impact?: string): string {
  const i = String(impact || '').toLowerCase();
  if (i === 'high') return 'bg-bear/15 text-bear';
  if (i === 'medium') return 'bg-warn/15 text-warn';
  return 'bg-bg-subtle text-fg-subtle';
}

/**
 * Top-bar notification bell. Pulls the shared `['news']` query and surfaces
 * every recent headline as a dropdown feed. The dropdown is portalled to the
 * document body so it floats above all page content (an absolutely-positioned
 * dropdown inside the sticky top bar gets clipped by the layout below it).
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [lastSeen, setLastSeen] = useState<number>(() => {
    try { return Number(localStorage.getItem(SEEN_KEY)) || 0; } catch { return 0; }
  });

  // Same queryKey as NewsPanel — React Query serves it from one shared cache.
  // Polls every 2 min so new headlines surface on their own — no page reload.
  const { data } = useQuery({
    queryKey: ['news'],
    queryFn: () => api.get<{ news: NewsItem[] }>('/api/market/news'),
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchIntervalInBackground: true,
  });

  // All headlines, newest first — not just high-impact, so the bell is a
  // complete alert feed. Capped so the dropdown stays scannable.
  const alerts = useMemo(() => {
    const items = data?.news ?? [];
    return items
      .slice()
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 12);
  }, [data]);

  const unread = useMemo(
    () => alerts.filter((n) => new Date(n.time).getTime() > lastSeen).length,
    [alerts, lastSeen],
  );

  // Flash the bell when the unread count rises while the panel is closed —
  // i.e. a fresh headline landed via the background poll.
  const prevUnread = useRef(unread);
  const [pinged, setPinged] = useState(false);
  useEffect(() => {
    if (unread > prevUnread.current && !open) {
      setPinged(true);
      const t = setTimeout(() => setPinged(false), 2400);
      prevUnread.current = unread;
      return () => clearTimeout(t);
    }
    prevUnread.current = unread;
  }, [unread, open]);

  // Keep the portalled dropdown glued to the bell on resize/scroll.
  useEffect(() => {
    if (!open) return;
    function place() {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setPinged(false);
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
      // Opening the panel marks everything currently shown as seen.
      if (alerts.length) {
        const newest = Math.max(...alerts.map((n) => new Date(n.time).getTime()));
        setLastSeen(newest);
        try { localStorage.setItem(SEEN_KEY, String(newest)); } catch { /* ignore */ }
      }
    }
  }

  // Navigate to the News panel. When an item is passed, stash its title as the
  // news search term so the panel opens filtered to that exact headline.
  function goToNews(item?: NewsItem) {
    setOpen(false);
    try {
      if (item) sessionStorage.setItem('telos_news_search', item.title);
      else sessionStorage.removeItem('telos_news_search');
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('telos:navigate-panel', { detail: 'news' }));
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        className={cn(
          'relative inline-flex items-center justify-center size-9 rounded-lg',
          'hover:bg-bg-card transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
          pinged ? 'text-bear' : 'text-fg-muted hover:text-fg',
        )}
      >
        <motion.span
          style={{ display: 'inline-flex' }}
          animate={pinged ? { rotate: [0, -16, 13, -9, 6, -3, 0] } : { rotate: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          <Bell className="size-4" />
        </motion.span>
        {pinged && (
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-lg ring-2 ring-bear/50"
            initial={{ opacity: 0.7, scale: 0.9 }}
            animate={{ opacity: 0, scale: 1.6 }}
            transition={{ duration: 1.1, repeat: 1, ease: 'easeOut' }}
          />
        )}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-bear px-1 text-[0.6rem] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && pos && (
            <>
              {/* Click-away catcher — above page content, below the dropdown */}
              <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                style={{ top: pos.top, right: pos.right }}
                // On mobile (<sm) we also anchor the LEFT edge to the
                // viewport (left-2) so the dropdown stretches across the
                // screen instead of pinning to the bell with a 320 px width
                // that overflowed off the left edge on small phones.
                // On desktop we fall back to a fixed 320 px right-anchored.
                className="fixed z-[91] left-2 sm:left-auto sm:w-80 max-w-[calc(100vw-1rem)] rounded-xl bg-bg-elevated border border-border shadow-[0_24px_52px_-12px_rgba(0,0,0,0.55)] overflow-hidden"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="text-sm font-semibold">Headline Alerts</span>
                  <span className="text-2xs text-fg-subtle">{alerts.length} recent</span>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {alerts.length === 0 ? (
                    <div className="px-4 py-10 text-center text-xs text-fg-muted">
                      No headlines right now.
                    </div>
                  ) : (
                    alerts.map((n) => {
                      const imp = String(n.impact || 'low').toLowerCase();
                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => goToNews(n)}
                          className="w-full text-left px-4 py-3 border-b border-border/60 last:border-0 hover:bg-bg-card transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span className={cn('inline-flex items-center gap-1 rounded text-[0.55rem] font-bold uppercase px-1.5 py-0.5', impactClass(imp))}>
                              {imp === 'high' && <AlertTriangle className="size-2.5" />}
                              {imp}
                            </span>
                            {(n.sentiment === 'bullish' || n.sentiment === 'bearish') && (
                              <span
                                className={cn(
                                  'rounded text-[0.55rem] font-bold uppercase px-1.5 py-0.5',
                                  n.sentiment === 'bullish' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
                                )}
                              >
                                {n.sentiment}
                              </span>
                            )}
                            <span className="ml-auto text-[0.6rem] text-fg-subtle">{timeAgo(n.time)}</span>
                          </div>
                          <div className="mt-1 text-xs text-fg leading-snug line-clamp-2">{n.title}</div>
                          <div className="mt-0.5 text-2xs text-fg-subtle">{n.source}</div>
                        </button>
                      );
                    })
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => goToNews()}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-accent hover:bg-bg-card transition-colors border-t border-border"
                >
                  View all news <ArrowRight className="size-3.5" />
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
